// @flow
import {
  AdSlot,
  GraphController,
  Grid,
  GridItem,
  MobileGraphController,
  Query,
  SeoData,
  SortableTable,
  useMedia,
} from '@haaretz/htz-components';
import React, { Fragment, } from 'react';
import { FelaTheme, } from 'react-fela';

import type { Node, } from 'react';

import PageRow from '../../components/PageRow/PageRow';
import QuoteInfoTable from '../../components/QuotePageComponents/QuoteInfoTable/QuoteInfoTable';
import QuoteSummary from '../../components/QuotePageComponents/QuoteSummary/QuoteSummary';
import RowItem from '../../components/RowItem/RowItem';
import MainLayout from '../../layouts/MainLayout';

import AdditionalInfo from '../../components/AdditionalInfo/AdditionalInfo';
import AssetStaticGraphs from '../../components/AssetStaticGraphs/AssetStaticGraphs';
import indexQuery from '../queries/indexData.graphql';
import useAllAdUnits from '../../components/AdManager/AdUnitsProvider';
import useAdUnitPathTemplate, { createAdSlotProps, } from '../../components/AdManager/useAdUnitPathTemplate';

type Props = {
  url: {
    pathname: string,
    query: {
      assetId: string,
      section: string,
    },
    asPath: string,
  },
  responseEnd: () => void,
  responseWriteHead: (statusCode: number, headers: { [key: string]: string | number | boolean, }) => void,
};

const numToString: number => string = num => num.toLocaleString('he', { minimumFractionDigits: 2, maximumFractionDigits: 2, });

function indices({
  url: {
    query: { section, assetId, },
    asPath,
  },
  responseEnd,
  responseWriteHead,
}: Props): Node {
  const isMobile = useMedia({ query: { from: 's', }, });
  const adUnits = useAllAdUnits();
  const adUnitPathTemplate = useAdUnitPathTemplate();

  return (
    <MainLayout section={section}>
      <Query query={indexQuery} variables={{ assetId, }}>
        {({ loading, error, data, }) => {
          if (error) return null;
          if (loading) return null;
          if (!data.assets?.length) return null;

          const type = data.assets[0]?.type?.toLowerCase();

          if (type && section !== type) {
            responseWriteHead(301, { Location: encodeURI(asPath.replace(section, type)), });
            responseEnd();

            return null;
          }

          const {
            name,
            value,
            changePercentage,
            numeralChange,
            subType,
            assetNumber,
            assetSummary,
            lastTradeTime,
            ...staticGraphsData
          } = data.assets[0];
          return (
            <FelaTheme>
              {theme => (
                <Fragment>
                  <SeoData
                    title={`${name} -מידע על מניות, בורסה ושוק ההון - TheMarker Finance`}
                    description={`כל המידע על ${name} נתוני מסחר, נתונים בזמן אמת, גרפים חדשות ועוד באתר TheMarker Finance`}
                    path={asPath}
                  />
                  <PageRow lines={2} miscStyles={{ position: 'relative', }}>
                    <RowItem
                      title={name}
                      miscStyles={{
                        ...theme.type(5),
                      }}
                    />
                    {
                      assetSummary
                        ? (
                          <AdditionalInfo
                            title="למידע נוסף על המדד"
                            info={assetSummary}
                            miscStyles={{
                              position: 'absolute',
                              left: '0',
                              bottom: '1rem',
                              zIndex: '2',
                              width: '41rem',
                            }}
                          />
                        )
                        : null
                    }
                  </PageRow>
                  <PageRow
                    miscStyles={{
                      marginBottom: '2rem',
                    }}
                  >
                    <QuoteSummary
                      valueData={[
                        { title: 'שער', value: value.toString(), },
                        { title: '% שינוי', value: changePercentage, },
                        { title: 'שינוי באג׳', value: numeralChange, },
                      ]}
                      date={{ title: 'נכון ל:', value: lastTradeTime, }}
                      assetInfo={[
                        { title: 'סוג נייר:', value: subType, },
                        { title: 'מספר נייר:', value: assetNumber, },
                      ]}
                    />
                  </PageRow>
                  <PageRow>
                    {
                      isMobile
                        ? <GraphController assetId={assetId} scatter width={900} />
                        : <MobileGraphController assetId={assetId} />
                    }
                  </PageRow>
                  <PageRow>
                    <Grid
                      gutter={2}
                      miscStyles={{
                        paddingStart: '0rem',
                        paddingEnd: '0rem',
                      }}
                    >
                      <QuoteInfoTable
                        id={assetId}
                        width={[
                          { until: 'l', value: 1, },
                          { from: 'l', value: 1 / 3, },
                        ]}
                        title="נתוני המסחר"
                        tradingStatus
                        fixed
                        fields={[
                          { name: 'baseValue', display: 'שער בסיס', },
                          { name: 'openingValue', display: 'שער פתיחה', },
                          { name: 'dailyLow', display: 'נמוך יומי', },
                          { name: 'dailyHigh', display: 'גבוה יומי', },
                          { name: 'standardDeviation', display: 'סטיית תקן', },
                          { name: 'peRatio', display: 'מכפיל רווח', },
                        ]}
                      />
                      <GridItem
                        width={[
                          { until: 'l', value: 1, },
                          { from: 'l', value: 2 / 3, },
                        ]}
                      >
                        <AssetStaticGraphs {...staticGraphsData} />
                      </GridItem>
                    </Grid>
                  </PageRow>
                  <PageRow>
                    <RowItem title="הרכב המדד">
                      <SortableTable
                        miscStyles={{ tableLayout: 'fixed', }}
                        filters={[
                          { section: 'type', subSection: subType === 'מדדי אג"ח' ? 'bond' : 'stock', },
                          { section: 'index', subSection: assetId, },
                        ]}
                        pagination
                        type="indices"
                        fragment="
                          id
                          type
                          name
                          value
                          changePercentage
                          volume
                          dailyHigh
                          dailyLow
                        "
                        fields={[
                          {
                            name: 'name',
                            display: 'שם נייר',
                            sortingOrder: 'asc',
                            style: () => ({
                              fontWeight: '700',
                              maxWidth: '17rem',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }),
                            value: ({ name, }) => name,
                          },
                          {
                            name: 'value',
                            display: 'שער אחרון',
                            sortingOrder: 'desc',
                            value: ({ value, }) => (value ? numToString(value) : '-'),
                          },
                          {
                            name: 'changePercentage',
                            display: '% שינוי',
                            sortingOrder: 'desc',
                            style: ({ changePercentage, }) => ({
                              ...(
                                changePercentage
                                  ? {
                                    color:
                                    changePercentage < 0
                                      ? theme.color('negative')
                                      : theme.color('positive'),
                                  }
                                  : {}
                              ),
                              direction: 'ltr',
                              fontWeight: '700',
                              paddingEnd: '2rem',
                              position: 'relative',
                              textAlign: 'start',
                            }),
                            value: ({ changePercentage, }) => `
                              ${changePercentage > 0 ? '' : changePercentage < 0 ? '-' : ''}
                              ${changePercentage ? numToString(Math.abs(changePercentage)) : changePercentage}%
                            `,
                          },
                          {
                            name: 'volume',
                            display: 'מחזור (א׳ ש״ח)',
                            sortingOrder: 'desc',
                            hideUntil: [ 'l', ],
                            value: ({ volume, }) => (volume ? numToString(volume) : '-'),
                          },
                          {
                            name: 'dailyHigh',
                            display: 'גבוה יומי',
                            sortingOrder: 'desc',
                            hideUntil: [ 'l', ],
                            value: ({ dailyHigh, }) => (dailyHigh ? numToString(dailyHigh) : '-'),
                          },
                          {
                            name: 'dailyLow',
                            display: 'נמוך יומי',
                            sortingOrder: 'desc',
                            hideUntil: [ 'l', ],
                            value: ({ dailyLow, }) => (dailyLow ? numToString(dailyLow) : '-'),
                          },
                        ]}
                        initialSort="name"
                        count={10}
                      />
                    </RowItem>
                  </PageRow>
                  <PageRow>
                    <RowItem>
                      <AdSlot {...createAdSlotProps(adUnits['finance.themarker.com.banner1'], adUnitPathTemplate)} />
                    </RowItem>
                  </PageRow>
                  <PageRow>
                    <RowItem title="קרנות סל המשקיעות במדד">
                      <SortableTable
                        miscStyles={{ tableLayout: 'fixed', }}
                        filters={[
                          { section: 'type', subSection: 'etf', },
                          { section: 'index', subSection: assetId, },
                        ]}
                        pagination
                        type="indices"
                        fragment="
                          id
                          type
                          name
                          value
                          changePercentage
                          volume
                          yearlyYield
                          issuerName
                          managementFee
                        "
                        fields={[
                          {
                            name: 'name',
                            display: 'שם נייר',
                            sortingOrder: 'asc',
                            style: () => ({
                              fontWeight: '700',
                              maxWidth: '17rem',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }),
                            value: ({ name, }) => name,
                          },
                          {
                            name: 'value',
                            display: 'שער אחרון',
                            sortingOrder: 'desc',
                            value: ({ value, }) => (value ? numToString(value) : '-'),
                          },
                          {
                            name: 'changePercentage',
                            display: '% שינוי',
                            sortingOrder: 'desc',
                            style: ({ changePercentage, }) => ({
                              ...(
                                changePercentage
                                  ? {
                                    color:
                                    changePercentage < 0
                                      ? theme.color('negative')
                                      : theme.color('positive'),
                                  }
                                  : {}
                              ),
                              direction: 'ltr',
                              fontWeight: '700',
                              paddingEnd: '2rem',
                              position: 'relative',
                              textAlign: 'start',
                            }),
                            value: ({ changePercentage, }) => `
                              ${changePercentage > 0 ? '' : changePercentage < 0 ? '-' : ''}
                              ${changePercentage ? `${numToString(Math.abs(changePercentage))}%` : '-'}
                            `,
                          },
                          {
                            name: 'volume',
                            display: 'מחזור (א׳ ש״ח)',
                            sortingOrder: 'desc',
                            hideUntil: [ 'l', ],
                            value: ({ volume, }) => (volume ? numToString(volume) : '-'),
                          },
                          {
                            name: 'yearlyYield',
                            display: 'תשואה שנתית',
                            sortingOrder: 'desc',
                            hideUntil: [ 'l', ],
                            style: ({ yearlyYield, }) => ({
                              ...(
                                yearlyYield
                                  ? {
                                    color:
                                    yearlyYield < 0
                                      ? theme.color('negative')
                                      : theme.color('positive'),
                                  }
                                  : {}
                              ),
                              direction: 'ltr',
                              fontWeight: '700',
                              paddingEnd: '2rem',
                              position: 'relative',
                              textAlign: 'start',
                            }),
                            value: ({ yearlyYield, }) => `
                              ${yearlyYield > 0 ? '' : yearlyYield < 0 ? '-' : ''}
                              ${yearlyYield ? `${numToString(Math.abs(yearlyYield))}%` : '-'}
                            `,
                          },
                          {
                            name: 'issuerName',
                            display: 'המנפיק',
                            sortingOrder: 'desc',
                            hideUntil: [ 'l', ],
                            value: ({ issuerName, }) => issuerName || '-',
                          },
                          {
                            name: 'managementFee',
                            display: 'דמי ניהול',
                            sortingOrder: 'desc',
                            hideUntil: [ 'l', ],
                            value: ({ managementFee, }) => (managementFee ? numToString(managementFee) : '-'),
                          },
                        ]}
                        initialSort="value"
                        count={5}
                      />
                    </RowItem>
                  </PageRow>
                  <PageRow>
                    <RowItem title="קרנות נאמנות המשקיעות במדד">
                      <SortableTable
                        miscStyles={{ tableLayout: 'fixed', }}
                        filters={[
                          { section: 'type', subSection: 'mtf', },
                          { section: 'index', subSection: assetId, },
                        ]}
                        pagination
                        type="indices"
                        fragment="
                          id
                          type
                          name
                          value
                          changePercentage
                          quarterlyYield
                          yearlyYield
                          issuerName
                          managementFee
                        "
                        fields={[
                          {
                            name: 'name',
                            display: 'שם נייר',
                            sortingOrder: 'asc',
                            style: () => ({
                              fontWeight: '700',
                              maxWidth: '17rem',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }),
                            value: ({ name, }) => name,
                          },
                          {
                            name: 'value',
                            display: 'שער אחרון',
                            sortingOrder: 'desc',
                            value: ({ value, }) => (value ? numToString(value) : '-'),
                          },
                          {
                            name: 'changePercentage',
                            display: '% שינוי',
                            sortingOrder: 'desc',
                            style: ({ changePercentage, }) => ({
                              ...(
                                changePercentage
                                  ? {
                                    color:
                                    changePercentage < 0
                                      ? theme.color('negative')
                                      : theme.color('positive'),
                                  }
                                  : {}
                              ),
                              direction: 'ltr',
                              fontWeight: '700',
                              paddingEnd: '2rem',
                              position: 'relative',
                              textAlign: 'start',
                            }),
                            value: ({ changePercentage, }) => `
                              ${changePercentage > 0 ? '' : changePercentage < 0 ? '-' : ''}
                              ${changePercentage ? `${numToString(Math.abs(changePercentage))}%` : '-'}
                            `,
                          },
                          {
                            name: 'quarterlyYield',
                            display: 'תשואה רבעונית',
                            sortingOrder: 'desc',
                            hideUntil: [ 'l', ],
                            style: ({ quarterlyYield, }) => ({
                              ...(
                                quarterlyYield
                                  ? {
                                    color:
                                    quarterlyYield < 0
                                      ? theme.color('negative')
                                      : theme.color('positive'),
                                  }
                                  : {}
                              ),
                              direction: 'ltr',
                              fontWeight: '700',
                              paddingEnd: '2rem',
                              position: 'relative',
                              textAlign: 'start',
                            }),
                            value: ({ quarterlyYield, }) => `
                              ${quarterlyYield > 0 ? '' : quarterlyYield < 0 ? '-' : ''}
                              ${quarterlyYield ? `${numToString(Math.abs(quarterlyYield))}%` : '-'}
                            `,
                          },
                          {
                            name: 'yearlyYield',
                            display: 'תשואה שנתית',
                            sortingOrder: 'desc',
                            hideUntil: [ 'l', ],
                            style: ({ yearlyYield, }) => ({
                              ...(
                                yearlyYield
                                  ? {
                                    color:
                                    yearlyYield < 0
                                      ? theme.color('negative')
                                      : theme.color('positive'),
                                  }
                                  : {}
                              ),
                              direction: 'ltr',
                              fontWeight: '700',
                              paddingEnd: '2rem',
                              position: 'relative',
                              textAlign: 'start',
                            }),
                            value: ({ yearlyYield, }) => `
                              ${yearlyYield > 0 ? '' : yearlyYield < 0 ? '-' : ''}
                              ${yearlyYield ? `${numToString(Math.abs(yearlyYield))}%` : '-'}
                            `,
                          },
                          {
                            name: 'issuerName',
                            display: 'המנפיק',
                            sortingOrder: 'desc',
                            hideUntil: [ 'l', ],
                            value: ({ issuerName, }) => issuerName,
                          },
                          {
                            name: 'managementFee',
                            display: 'דמי ניהול',
                            sortingOrder: 'desc',
                            hideUntil: [ 'l', ],
                            value: ({ managementFee, }) => (managementFee ? numToString(managementFee) : '-'),
                          },
                        ]}
                        initialSort="value"
                        count={5}
                      />
                    </RowItem>
                  </PageRow>
                  <PageRow>
                    <RowItem>
                      <AdSlot {...createAdSlotProps(adUnits['finance.themarker.com.banner2'], adUnitPathTemplate)} />
                    </RowItem>
                  </PageRow>
                </Fragment>
              )}
            </FelaTheme>
          );
        }}
      </Query>
    </MainLayout>
  );
}

export default indices;
